import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { listFiltersService, ListTableSort, getSortedCollection } from "sprint-common-web";

import { TvSeasonDetails, TvSeriesDetails } from "../../../TvSeriesDetails";
import { TvSeriesContext } from "../../context/TvSeriesContext";
import { defaultSeasonListingFilters, SeasonListingFilters } from "../season-listing/SeasonListingFilters";
import { mapToSeasonListingItem, SeasonListingItem } from "../season-listing/SeasonListingItem";
import { TvSeriesPageContext } from "./TvSeriesPageContext";

type TvSeriesPageProviderProps = React.PropsWithChildren<unknown>;

export const TvSeriesPageProvider: FunctionComponent<TvSeriesPageProviderProps> = (
    props: TvSeriesPageProviderProps
) => {
    const { children } = props;

    const tvSeriesDetailsContext = useContext(TvSeriesContext);
    const [listingItems, setListingItems] = useState<SeasonListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [seasonsToMerge, setSeasonsToMerge] = useState<SeasonListingItem[]>([]);
    const isLoading = tvSeriesDetailsContext.isLoading;
    const filtersService = useMemo(() => listFiltersService<SeasonListingFilters>(defaultSeasonListingFilters), []);
    const location = useLocation();
    const history = useHistory();
    const [filters, setFilters] = useState<SeasonListingFilters>(() => filtersService.getSavedFilters(location.search));

    useEffect(() => {
        if (!isLoading && filters) {
            setFilteredAndSortedListingItems(filters);
        }
    }, [filters, isLoading]);

    const addNewSeason = (newSeason: TvSeasonDetails) => {
        if (!tvSeriesDetailsContext.tvSeries) return;

        if (listingItems.length >= filters.pageSize) listingItems.pop();
        const newListingItems = [mapToSeasonListingItem(newSeason, newSeason.isModified), ...listingItems];
        setListingItems(newListingItems);

        tvSeriesDetailsContext.addSeason(newSeason);
    };

    const editTvSeries = (title: string, year?: number) => {
        tvSeriesDetailsContext.setTvSeriesDetails(title, year);
    };

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({
            sortBy: sort.by as keyof SeasonListingItem,
            sortOrder: sort.order,
            page: 1
        });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setOrphanedOnly = (orphanedOnly?: boolean): void => {
        saveAndSetFilters({ orphanedOnly });
    };

    const setIncludeMerged = (includeMerged?: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    const saveAndSetFilters = (newFilters: Partial<SeasonListingFilters>): void => {
        setFilters((prevFilters: SeasonListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultSeasonListingFilters);
    };

    const setFilteredAndSortedListingItems = (filter: SeasonListingFilters, tvSeries?: TvSeriesDetails) => {
        const seasons = tvSeries?.seasons ?? tvSeriesDetailsContext.tvSeries?.seasons ?? [];

        const filteredItems = getFilteredSeasonItems(mapToSeasonItems(seasons), filter);
        setTotalItems(filteredItems.length);

        const sortedItems = getSortedCollection(filteredItems, filter.sortBy, filter.sortOrder);
        const currentPageItems = getCurrentPageItems(sortedItems, filter);

        setListingItems(currentPageItems);
    };

    const getCurrentPageItems = (items: SeasonListingItem[], filter: SeasonListingFilters): SeasonListingItem[] => {
        const firstItem = (filter.page - 1) * filter.pageSize;
        const lastItem = filter.page * filter.pageSize;

        return items.slice(firstItem, lastItem);
    };

    const getFilteredSeasonItems = (items: SeasonListingItem[], filter: SeasonListingFilters): SeasonListingItem[] => {
        let filteredSeasons = items;

        filteredSeasons = filter.includeMerged
            ? filteredSeasons
            : filteredSeasons.filter((version: SeasonListingItem) => !version.merged === !filter.includeMerged);

        filteredSeasons = filter.orphanedOnly
            ? filteredSeasons.filter((season: SeasonListingItem) => season.orphaned)
            : filteredSeasons;

        return filteredSeasons;
    };

    const removeSeasonFromMergeList = (seasonToRemove: SeasonListingItem) => {
        setSeasonsToMerge((prevSeasons: SeasonListingItem[]) => {
            return prevSeasons.filter((season: SeasonListingItem) => seasonToRemove.id !== season.id);
        });
    };

    const updateListAfterMerge = async (newSeasonId: string): Promise<void> => {
        const updateSeasonListAfterMerge = (tvSeries: TvSeriesDetails): void => {
            setFilteredAndSortedListingItems(filters, tvSeries);

            setListingItems((prevItems: SeasonListingItem[]) => {
                const newSeason = tvSeries.seasons.find((season: TvSeasonDetails) => season.id === newSeasonId);
                if (!newSeason) return prevItems;

                newSeason.isModified = true;
                return getListWithNewItemOnTop(prevItems, filters, newSeason);
            });
        };

        await tvSeriesDetailsContext.silentTvSeriesRefresh(updateSeasonListAfterMerge);
    };

    const addSeasonToMergeList = (item: SeasonListingItem) => {
        setSeasonsToMerge((prevSeasonItems: SeasonListingItem[]) => {
            return [...prevSeasonItems, item];
        });
    };

    const clearSeasonsToMerge = () => {
        setSeasonsToMerge([]);
    };

    return (
        <TvSeriesPageContext.Provider
            value={{
                listingItems,
                isLoading,
                totalItems,
                seasonsToMerge,
                clearFilters,
                setSort,
                setPage,
                setPageSize,
                setOrphanedOnly,
                setIncludeMerged,
                addNewSeason,
                editTvSeries,
                removeSeasonFromMergeList,
                updateListAfterMerge,
                addSeasonToMergeList,
                clearSeasonsToMerge,
                filters,
                tvSeries: tvSeriesDetailsContext.tvSeries
            }}
        >
            {children}
        </TvSeriesPageContext.Provider>
    );
};

const mapToSeasonItems = (seasons: TvSeasonDetails[], resetNewFlag = true): SeasonListingItem[] => {
    return seasons.map((season: TvSeasonDetails) =>
        mapToSeasonListingItem(season, resetNewFlag ? false : season.isModified)
    );
};

const getListWithNewItemOnTop = (
    itemList: SeasonListingItem[],
    filters: SeasonListingFilters,
    newSeason: TvSeasonDetails
) => {
    if (itemList.length >= filters.pageSize) itemList.pop();
    const filteredPrevItems = itemList.filter((item: SeasonListingItem) => item.id !== newSeason.id);
    return [mapToSeasonListingItem(newSeason, false), ...filteredPrevItems];
};
